<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import {
  createPersonalNewsHomeShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { usePersonalFeedSummary } from '@/components/feed-summary/composables/use-personal-feed-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button-next.vue';
import FeedSummaryNext from '@/components/feed-summary/feed-summary-next.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { AdpDocument, Article } from '@/types';
import { balancedFeedCounts } from '@/utils';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { formatDate } from '@/utils/formatters';
import {
  executionInformationForDate,
  getMorningFeedData,
  isAfternoonFeedEnabled,
  latestPersonalFeedDatetime,
} from '@/utils/personalNews';
import {
  STATES,
  usePersonalNewsMultipleExecution,
  useUserInfo,
} from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const layoutVariant = 'next';

const store = useStore();
const emitter = useEmitter();
const { data: userInfo, mutate: mutateUserInfo } = useUserInfo();

const dateStr = formatDate(dayjs().toISOString());

const feedType = computed(() => store.state.feedType.feedType);

const TOTAL_VISIBLE_ARTICLE_COUNT = 4;
const visibleFeedCount = ref(TOTAL_VISIBLE_ARTICLE_COUNT);

const sentFirstPersonalNewsFeedAt = computed(() =>
  userInfo.value?.sent_first_personal_news_feed_at
    ? dayjs(userInfo.value?.sent_first_personal_news_feed_at)
    : undefined,
);

const feedLastUpdatedAt = computed(() =>
  latestPersonalFeedDatetime(sentFirstPersonalNewsFeedAt.value),
);

const removedArticleIds = ref([] as number[]);
emitter.on('personal-feed-article-removed', (article: Article) => {
  removedArticleIds.value.push(article.id);
});
emitter.on('personal-feed-article-restored', (article: Article) => {
  removedArticleIds.value = removedArticleIds.value.filter(
    articleId => articleId !== article.id,
  );
});

const executionInfoMorning = computed(() =>
  executionInformationForDate(
    feedLastUpdatedAt.value,
    'morning',
    sentFirstPersonalNewsFeedAt.value,
  ),
);
const executionInfoAfternoon = computed(() =>
  executionInformationForDate(
    feedLastUpdatedAt.value,
    'afternoon',
    sentFirstPersonalNewsFeedAt.value,
  ),
);

const shouldShowAfternoonFeed = computed(
  () =>
    (isAfternoonFeedEnabled(feedLastUpdatedAt.value) ||
      executionInfoAfternoon.value.isFirstExecution) &&
    !executionInfoMorning.value.isFirstExecution,
);

const execution = computed(() =>
  shouldShowAfternoonFeed.value ? 'afternoon' : 'morning',
);

const feedData = computed(() =>
  execution.value === 'morning'
    ? getMorningFeedData(
        feedLastUpdatedAt.value,
        sentFirstPersonalNewsFeedAt.value,
      )
    : { date: feedLastUpdatedAt.value, execution: 'afternoon' },
);

const { data: domesticFeed, state: domesticState } =
  usePersonalNewsMultipleExecution(
    feedType,
    'domestic',
    feedData.value.date,
    feedData.value.execution,
    ref(1),
    visibleFeedCount,
  );
const { data: foreignFeed, state: foreignState } =
  usePersonalNewsMultipleExecution(
    feedType,
    'foreign',
    feedData.value.date,
    feedData.value.execution,
    ref(1),
    visibleFeedCount,
  );

const allArticles = computed(() => {
  const domesticArticles = (domesticFeed.value ?? { personal_feeds: [] })
    .personal_feeds;
  const foreignArticles = (foreignFeed.value ?? { personal_feeds: [] })
    .personal_feeds;
  const [domesticCount, foreignCount] = balancedFeedCounts(
    domesticArticles.length,
    foreignArticles.length,
    TOTAL_VISIBLE_ARTICLE_COUNT,
  );
  return [
    ...domesticArticles.slice(0, domesticCount),
    ...foreignArticles.slice(0, foreignCount),
  ];
});

const articles = computed(() =>
  allArticles.value?.filter(a => !removedArticleIds.value.includes(a.id)),
);

const isLoaded = computed(() => {
  if (feedType.value === 'all') {
    return (
      domesticState.value === STATES.SUCCESS &&
      foreignState.value === STATES.SUCCESS
    );
  } else if (feedType.value === 'domestic') {
    return domesticState.value === STATES.SUCCESS;
  } else {
    return foreignState.value === STATES.SUCCESS;
  }
});

const articleCount = computed(() => {
  return (domesticFeed.value?.count ?? 0) + (foreignFeed.value?.count ?? 0);
});

const {
  summary,
  generateButtonState,
  generateSummary,
  summarySuffix,
  mutate: mutatePersonalFeedSummary,
} = usePersonalFeedSummary(
  {
    page_type: 'feed',
    feed_type: feedType.value,
    doc_type: 'article',
    date: feedLastUpdatedAt.value.format('YYYY-MM-DD'),
    execution: execution.value,
  },
  articleCount,
  'home',
);

useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  'personal_feed' as const,
  createPersonalNewsHomeShouldEnable(),
);

const optionMenu = useAdpDocumentOptionMenu({
  pageName: 'home',
  feature: 'personal_feed',
  enablePersonalFeedSiteExclusion: true,
});
const personalFeedConfig = { notInterested: true, excludeMedia: true };

const hasArticles = computed(() => articles.value.length > 0);

const { setRef, requestId } = useHomeSectionViewTracking(
  'personal_feed_view',
  isLoaded,
  () => ({
    document_count: articles.value.length,
    layout_variant: layoutVariant,
  }),
);

const noArticlesMessage = computed(() =>
  execution.value === 'morning'
    ? '次回の更新は本日13:00です。'
    : '次回の更新は明日7:00です。',
);

const syncArticle = (updated: AdpDocument) =>
  syncAdpDocumentActions(articles.value ?? [], updated);
const onArticleUpdated = (updated: AdpDocument) => {
  mutateUserInfo();
  mutatePersonalFeedSummary();
  syncArticle(updated);
};
onMounted(() => {
  emitter.on('article-updated', onArticleUpdated);
  emitter.on('article-viewed', syncArticle);
});
onUnmounted(() => {
  emitter.off('article-updated', onArticleUpdated);
  emitter.off('article-viewed', syncArticle);
});
</script>
<template>
  <div class="personal-news aconnect-home-component" :ref="setRef">
    <div class="header">
      <span class="title">
        <DgrIcon
          name="sparkles-fill"
          :keep-fill="false"
          class="aconnect-home-icon animated"
        />
        <router-link :to="{ name: 'personalNews' }" class="c-title-bold--xm"
          >パーソナルニュース</router-link
        >
        <span class="c-text-regular--s subtitle">{{ dateStr }}</span>
        <span class="c-text-regular--s subtitle"
          >AIが興味に合う記事を毎日お届け。マークするほど精度が上がります。</span
        >
      </span>
      <FeedSummaryGenerationButton
        :state="generateButtonState"
        :summary-time="summarySuffix"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <div v-if="!isLoaded" class="loading-container">
      <DgrLoading class="loading" />
    </div>
    <template v-else>
      <div v-if="hasArticles" class="feed">
        <div class="featured-wrapper">
          <AdpDocumentCardSmall
            class="featured document-card"
            :key="articles[0].id"
            :id="`${articles[0].id}`"
            :adp-document="articles[0]"
            image-variant="banner"
            :feed-type="feedType"
            show-published-date
            show-option-menu
            :option-actions="
              optionMenu.buildOptionActions(articles[0], personalFeedConfig)
            "
            page-name="home"
            feature="personal_feed"
            mark-source="personal_feed"
            @option-action="
              name => optionMenu.handleOptionAction(name, articles[0])
            "
            :contents-context="{
              event_name: 'personal_feed',
              parent_request_id: requestId,
            }"
          />
          <span class="c-text-regular--xs featured-tag">今日の注目記事</span>
        </div>
        <FeedSummaryNext
          v-if="summary"
          class="feed-summary"
          :feed-summary="summary"
          :suffix="summarySuffix"
          :show-message-when-closed="true"
          :auto-open-when-generated="true"
          feature="personal_feed"
          page-name="home"
        />
        <div class="documents">
          <AdpDocumentCardSmall
            v-for="article in articles.slice(1)"
            :key="article.id"
            :id="`${article.id}`"
            class="document-card"
            :adp-document="article"
            :feed-type="feedType"
            show-published-date
            show-option-menu
            :option-actions="
              optionMenu.buildOptionActions(article, personalFeedConfig)
            "
            page-name="home"
            feature="personal_feed"
            mark-source="personal_feed"
            @option-action="
              name => optionMenu.handleOptionAction(name, article)
            "
            :contents-context="{
              event_name: 'personal_feed',
              parent_request_id: requestId,
            }"
          />
        </div>
      </div>
      <div v-else class="empty">
        <img src="@/assets/state-empty-article.png" class="empty-img" />
        <div class="c-title-medium--xm title">
          今回はおすすめできる記事がありませんでした
        </div>
        <div class="c-text-regular--s">{{ noArticlesMessage }}</div>
        <div class="c-text-regular--xs">
          記事を閲覧・マーク・コメントすると興味を学習し、おすすめの精度が上がります。
        </div>
      </div>
    </template>
    <div class="footer" :class="{ 'footer--empty': !hasArticles }">
      <router-link
        :to="{ name: 'personalNews' }"
        class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small"
      >
        {{
          hasArticles || !isLoaded
            ? 'パーソナルニュースをすべて見る'
            : '過去の配信を見る'
        }}
      </router-link>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.personal-news {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px 16px 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .header {
    display: flex;
    justify-content: space-between;

    .title {
      display: flex;
      gap: 8px;
      align-items: center;

      a:hover {
        color: $color-green600;
        text-decoration: underline;
      }

      .icon-box {
        color: $color-green600;
      }

      .subtitle {
        color: $color-gray800;
      }
    }
  }

  .feed {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'featured  summary   summary'
      'documents documents documents';
    gap: 16px;

    .featured-wrapper {
      position: relative;

      .featured-tag {
        position: absolute;
        top: 8px;
        left: 8px;
        padding: 2px 8px;
        border-radius: 4px;
        color: #ffffff;
        background: $color-green600;
        box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
        pointer-events: none;
      }
    }
    .featured {
      grid-area: featured;
      min-width: 0;
      border: 1px solid $color-gray400;
      border-radius: 4px;

      &:has(.document-info-image a:hover) {
        border-color: $color-gray600;
      }
    }
    .feed-summary {
      grid-area: summary;
      contain: size;
    }

    .documents {
      grid-area: documents;
      display: grid;
      grid-template-columns: subgrid;

      .document-card {
        min-width: 0;
        padding: 16px;
        border: 1px solid $color-gray400;
        border-radius: 4px;

        &:has(.document-info-image a:hover) {
          border-color: $color-gray600;
        }
      }
    }
  }

  .loading-container {
    width: fit-content;
    height: 400px;
    margin: 0 auto;
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    .empty-img {
      width: 132px;
      height: 80px;
      object-fit: cover;
    }
    .c-title-medium--xm {
      margin-bottom: 8px;
    }
  }

  .footer {
    display: flex;
    justify-content: center;

    &.footer--empty {
      margin-top: 4px;
      margin-bottom: 12px;
    }
  }
}
</style>
